import { AfterContentChecked, Component, ElementRef, inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core'
import { NgForm, FormsModule } from '@angular/forms'
import { finalize, zipWith } from 'rxjs'
import { tap } from 'rxjs/operators'

import { PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { Divider } from 'primeng/divider'
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { FileUpload } from 'primeng/fileupload'
import { Message } from 'primeng/message'
import { Select } from 'primeng/select'
import { Tag } from 'primeng/tag'
import { Textarea } from 'primeng/textarea'
import SignaturePad from 'signature_pad'

import { UserService } from '@app/services/user.service'
import { UtilitiesService } from '@app/services/utilities.service'
import { Localizable, StaticDropDown } from '@app/components/generic/generic'
import {
  LOGISTICS_ASSET_ACCESSORIES, LOGISTICS_ASSET_CATEGORIES, LOGISTICS_ASSET_CONDITIONS, LOGISTICS_ASSET_SUB_CATEGORIES,
} from '@app/components/logistics/assets/assets.constants'
import { AssetAllocationPremises } from '@app/components/logistics/assets-allocations-premises/assets-allocations-premises'
import { AssetAllocationUsage } from '@app/components/logistics/assets-allocations-usages/assets-allocations-usages'
import { SessionUser } from '@app/components/roles/user.utils'

import { Confirmation, newConfirmation } from './confirmations'
import { ConfirmationsApi } from './confirmations.api'
import { CONFIRMATION_METHODS, CONFIRMATION_TYPES, ConfirmationMethod, ConfirmationTransition, ConfirmationType } from './confirmations.constants'

@Component({
  templateUrl: 'confirmations-workflow-modal.html',
  styleUrl: 'confirmations-workflow-modal.css',
  imports: [
    FormsModule,
    Message,
    Tag,
    Divider,
    Select,
    FileUpload,
    PrimeTemplate,
    Textarea,
    Button,
  ],
})
export class ConfirmationModalComponent implements OnInit, AfterContentChecked {
  readonly ref = inject(DynamicDialogRef)
  private readonly confirmationsApi = inject(ConfirmationsApi)
  private readonly config = inject(DynamicDialogConfig)
  private readonly locale = inject(LOCALE_ID)
  private readonly userService = inject(UserService)
  protected readonly utilitiesService = inject(UtilitiesService)

  detail: Confirmation = newConfirmation()
  method?: ConfirmationMethod
  loading = false

  protected readonly ConfirmationMethod = ConfirmationMethod
  protected readonly ConfirmationType = ConfirmationType
  protected readonly CONFIRMATION_TYPES = CONFIRMATION_TYPES
  protected readonly LOGISTICS_ASSET_CATEGORIES = LOGISTICS_ASSET_CATEGORIES
  protected readonly LOGISTICS_ASSET_SUB_CATEGORIES = LOGISTICS_ASSET_SUB_CATEGORIES
  protected readonly LOGISTICS_ASSET_CONDITIONS = LOGISTICS_ASSET_CONDITIONS
  protected readonly LOGISTICS_ASSET_ACCESSORIES = LOGISTICS_ASSET_ACCESSORIES
  protected confirmationMethods: StaticDropDown[] = []
  protected signaturePad?: SignaturePad
  protected labelField: keyof Localizable = 'label_en'
  private user?: SessionUser

  @ViewChild('f', { static: true }) public detailForm!: NgForm
  @ViewChild('fileInput') public fileInput?: FileUpload
  @ViewChild('drawingCanvas') protected drawingCanvas?: ElementRef<HTMLCanvasElement>

  constructor () {
    if (this.locale === 'fr') {
      this.labelField = 'label_fr'
    }
  }

  ngAfterContentChecked (): void {
    if (this.drawingCanvas && !this.signaturePad) {
      this.signaturePad = new SignaturePad(this.drawingCanvas.nativeElement)
      this.resizeCanvas(this.signaturePad, this.drawingCanvas.nativeElement)
    }
    if (!this.drawingCanvas) {
      this.signaturePad = undefined
    }
  }

  ngOnInit () {
    this.confirmationsApi.retrieve(this.config.data.code).pipe(
      // Set the retrieved Confirmation as this modal's `detail`.
      tap(confirmation => (this.detail = confirmation)),
      // Retrieve current user.
      zipWith(this.userService.getUser()),
      tap(([_, user]) => (this.user = user)),
      // Set methods drop down but filter-out methods not supported by this Confirmation.
      tap(([confirmation, user]) => {
        this.confirmationMethods = this.getConfirmationMethods(confirmation, user)
      }),
      // Initialize method if necessary.
      tap(_ => {
        if (this.methodMustBeCore()) {
          this.method = ConfirmationMethod.CORE_USER
        } else if (this.confirmationMethods.length) {
          // select the first available method by default
          this.method = this.confirmationMethods[0].value as ConfirmationMethod
        }
      }),
    ).subscribe()
  }

  private getConfirmationMethods (confirmation: Confirmation, user: SessionUser) {
    return CONFIRMATION_METHODS
      .filter(method => confirmation.allowed_methods?.includes(method.value))
      .filter(method => {
        if (method.value !== ConfirmationMethod.CORE_USER) return true
        return confirmation.confirmation_staff_relations?.map(relation => relation.confirming_staff.email).includes(user.email)
      })
  }

  protected methodMustBeCore () : boolean {
    return this.detail.allowed_methods.includes(ConfirmationMethod.CORE_USER) &&
      this.confirmationMethods.map(m => m.value).includes(ConfirmationMethod.CORE_USER) &&
      !!this.user &&
      !!this.detail.confirmation_staff_relations?.map(relation => relation.confirming_staff.email).includes(this.user.email)
  }

  /**
   * reject the confirmation
   */
  onReject () {
    this.loading = true
    this.confirmationsApi.play(this.detail.code!, ConfirmationTransition.REJECT, {
      rejection_reason: this.detail.rejection_reason,
    }).pipe(
      finalize(() => {
        // force the reload for success and fail operations
        this.ref.close(this.detail)
        this.loading = false
      }),
    ).subscribe()
  }

  /**
   * accept the confirmation and upload any files
   */
  onAccept () {
    this.loading = true
    let files: File[] = []
    if (this.method === ConfirmationMethod.AST_04) {
      files = this.fileInput?.files ?? []
    } else if (this.method === ConfirmationMethod.DRAWING) {
      files = [new File([this.dataUrlToBlob(this.signaturePad!.toDataURL())], 'signature.png')]
    }
    this.confirmationsApi.acceptWithUpload(this.detail.code!, files, {}).pipe(
      finalize(() => {
        // force the reload for success and fail operations
        this.ref.close(this.detail)
        this.loading = false
      }),
    ).subscribe()
  }

  onCancel () {
    this.ref.close()
  }

  private dataUrlToBlob (dataUrl: string): Blob {
    const mimeType = dataUrl.split(',')[0].split(':')[1].split(';')[0]
    const binaryString = atob(dataUrl.split(',')[1])
    const byteArray: number[] = []
    for (let i = 0; i < binaryString.length; i++) {
      byteArray.push(binaryString.charCodeAt(i))
    }
    return new Blob([new Uint8Array(byteArray)], { type: mimeType })
  }

  private resizeCanvas (signaturePad: SignaturePad, canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d')
    const canvasData = signaturePad.toData()
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    canvas.width = canvas.offsetWidth * ratio
    canvas.height = canvas.offsetHeight * ratio
    context?.scale(ratio, ratio)
    if (canvasData.length) signaturePad.fromData(canvasData)
  }

  protected isAllocationPremises (content: AssetAllocationUsage | AssetAllocationPremises | undefined): content is AssetAllocationPremises {
    return !!content && 'premises' in content
  }
}
